import {
  ReactElement,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Stack, FormHelperText, Typography, Button } from '@mui/material';
import { useFormikContext, FieldArray, ArrayHelpers } from 'formik';
import { DndProvider, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { find, get, indexOf, includes } from 'lodash';

import { Nullable, SelectOption } from '~globals/types';
import { useToggle } from '~hooks/useToggle';
import { getHelperText } from '~utils/formHelpers';

import DialogConfimation from '../DialogConfimation';
import CustomAutocomplete from '../CustomAutocomplete';

import RoadmapColumnsFieldSortableItem from './SortableItem';
import { RoadmapColumnsFieldProps } from './types';
import { ITEM_TYPES_DND } from './utils';
import {
  RoadmapColumnsFieldEmptyContainer,
  RoadmapColumnsFieldItemChip,
} from './styles';

const RoadmapColumnsField = ({
  name,
  options,
  disabled = false,
}: RoadmapColumnsFieldProps): ReactElement => {
  const { values, errors, touched, setFieldTouched } = useFormikContext<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: string;
  }>();

  const selectedColumnsList = useMemo(
    () => values[name] as unknown as string[],
    [values, name],
  );

  const findCurrentListColumnIndex = useCallback(
    (columnId: string) => indexOf(selectedColumnsList, columnId),
    [selectedColumnsList],
  );

  const getSelectedColumnLabel = useCallback(
    (columnId: string) => {
      const currentColumnData = find(options, { value: columnId });

      return get(currentColumnData, 'label', '').toString();
    },
    [options],
  );

  const getAutocompleteOptionDisabled = useCallback(
    (currentOpton: SelectOption) =>
      includes(selectedColumnsList, currentOpton.value.toString()),
    [selectedColumnsList],
  );

  const error = useMemo(
    () => getHelperText(touched, errors, name),
    [errors, name, touched],
  );

  const [newColumn, setNewColumn] = useState<Nullable<SelectOption>>(null);

  const [openAddColumnDialog, toggleAddColumnDialog] = useToggle(false);

  const handleManualCloseAddColumnDialog = useCallback(() => {
    setNewColumn(null);
    toggleAddColumnDialog(false);
  }, [toggleAddColumnDialog]);

  const handleChangeNewColumn = useCallback(
    (_event: SyntheticEvent, value: Nullable<SelectOption>) => {
      setNewColumn(value);
    },
    [],
  );

  const handleAddColumInList = useCallback(
    (insertHelper: ArrayHelpers['insert']) => {
      if (newColumn) {
        const insertPositionIndex = selectedColumnsList.length;

        insertHelper(insertPositionIndex, newColumn.value);

        setFieldTouched(name, true);

        setNewColumn(null);

        handleManualCloseAddColumnDialog();
      }
    },
    [
      name,
      newColumn,
      selectedColumnsList.length,
      setFieldTouched,
      handleManualCloseAddColumnDialog,
    ],
  );

  const handleMoveColumInList = useCallback(
    (moveHelper: ArrayHelpers['move'], columnId: string, atIndex: number) => {
      const currentIndex = findCurrentListColumnIndex(columnId);

      moveHelper(currentIndex, atIndex);

      setFieldTouched(name, true);
    },
    [findCurrentListColumnIndex, name, setFieldTouched],
  );

  const handleRemoveColumnInList = useCallback(
    (removeHelper: ArrayHelpers['remove'], columnIndex: number) => {
      removeHelper(columnIndex);

      setFieldTouched(name, true);
    },
    [name, setFieldTouched],
  );

  const [, dropConnect] = useDrop(() => ({
    accept: ITEM_TYPES_DND.ROADMAP_COLUM_BOX,
  }));

  return (
    <FieldArray name={name}>
      {({ insert, move, remove }) => (
        <Stack>
          {selectedColumnsList.length === 0 && (
            <RoadmapColumnsFieldEmptyContainer>
              <Stack spacing={0.5}>
                <Typography variant="body2" fontWeight="medium">
                  Todavía no cargaste ninguno
                </Typography>

                <Typography variant="caption">
                  Por ejemplo: NRO. DE ORDEN, VENTANA HORARIA, DOMICILIO.
                </Typography>
              </Stack>

              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => toggleAddColumnDialog(true)}
                sx={{ backgroundColor: 'common.white' }}
              >
                Agregar la primera columna
              </Button>
            </RoadmapColumnsFieldEmptyContainer>
          )}

          {selectedColumnsList.length > 0 && (
            <Stack ref={dropConnect} gap={1} direction="row" flexWrap="wrap">
              {selectedColumnsList.map((columnId, columnIndex) => (
                <RoadmapColumnsFieldSortableItem
                  key={`roadmap-column-item-sortable-${columnId}`}
                  columnId={columnId}
                  value={getSelectedColumnLabel(columnId)}
                  findColumnIndex={findCurrentListColumnIndex}
                  onMove={(moveColumnId, atIndex) =>
                    handleMoveColumInList(move, moveColumnId, atIndex)
                  }
                  onRemove={() => handleRemoveColumnInList(remove, columnIndex)}
                  disabled={disabled}
                />
              ))}

              <RoadmapColumnsFieldItemChip
                variant="outlined"
                size="medium"
                color="primary"
                label="+ Agregar"
                disabled={disabled}
                onClick={() => toggleAddColumnDialog(true)}
              />
            </Stack>
          )}

          {error && (
            <FormHelperText variant="outlined" error>
              {error}
            </FormHelperText>
          )}

          <DialogConfimation
            open={openAddColumnDialog}
            title="Agregar columna"
            // eslint-disable-next-line max-len
            description="Personaliza la información que se exporta en las documentos de ruta según la necesidad de tu empresa."
            cancelText="Cancelar"
            onCancel={handleManualCloseAddColumnDialog}
            confirmText="Agregar"
            onConfirm={() => handleAddColumInList(insert)}
          >
            <CustomAutocomplete
              options={options}
              label="Nueva columna"
              InputLabelProps={{ shrink: true }}
              filterSelectedOptions
              size="small"
              value={newColumn}
              onChange={handleChangeNewColumn}
              getOptionDisabled={getAutocompleteOptionDisabled}
              fullWidth
              disabled={disabled}
            />
          </DialogConfimation>
        </Stack>
      )}
    </FieldArray>
  );
};

const WrappedRoadmapColumnsField = (
  props: RoadmapColumnsFieldProps,
): ReactElement => (
  <DndProvider backend={HTML5Backend}>
    <RoadmapColumnsField {...props} />
  </DndProvider>
);

export default WrappedRoadmapColumnsField;
